// src/components/admin/ProductsEditor.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import ImageCropModal from './ImageCropModal';
import { storage } from '../../utils/firebase';
import { useSalonLocations } from '../../hooks/useSalonLocations';
import DeliveryChargesEditor from './DeliveryChargesEditor';
import {
  getSalonProducts,
  addSalonProduct,
  updateSalonProduct,
  deleteSalonProduct,
  updateProductInventory,
  upsertProductAlert,
  resolveProductAlert,
  getProductProspects,
  getProductsPayNowQrUrl
} from '../../api/productsApi';
import '../../styles/products.css';
import '../../styles/services.css';

const PRODUCT_CATEGORIES = ['Hair Care', 'Skin Care', 'Nail Care', 'Body Care', 'Accessories', 'Other'];

const EMPTY_FORM = {
  name: '',
  description: '',
  category: 'Hair Care',
  price: 0,
  imageUrl: '',
  locationIds: [],
  isActive: true
};

// Days until renewal date
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function ProductsEditor({ salonId, onUpdate, savedMessage, onPreview, onNavigateToCampaign }) {
  const [activeTab, setActiveTab] = useState('catalog');

  // ── Catalog state ──
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState({ name: '', price: '' });
  const [cropFile, setCropFile] = useState(null); // file waiting to be framed
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');

  // ── Locations ──
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;

  // ── Prospects state ──
  // Primary criteria
  const [criteriaType, setCriteriaType] = useState('spend_product');
  const [spendThreshold, setSpendThreshold] = useState('');
  const [prospectsProductId, setProspectsProductId] = useState('');
  const [prospectsCategory, setProspectsCategory] = useState(PRODUCT_CATEGORIES[0]);
  const [lastPurchaseDate, setLastPurchaseDate] = useState('');
  // Additional criteria (up to 2)
  const [additionalCriteria, setAdditionalCriteria] = useState([]);
  const [prospectsLoading, setProspectsLoading] = useState(false);
  const [prospectsError, setProspectsError] = useState('');
  const [showProspectsModal, setShowProspectsModal] = useState(false);
  const [prospectsResult, setProspectsResult] = useState(null);
  const [prospectsFilter, setProspectsFilter] = useState('');
  const [paynowQrUrl, setPaynowQrUrl] = useState('');

  // ── Inventory state ──
  const [inventoryData, setInventoryData] = useState({});
  const [inventorySaving, setInventorySaving] = useState({});
  const [inventorySaved, setInventorySaved] = useState({});
  const [inventoryError, setInventoryError] = useState('');

  // ── Load ──
  useEffect(() => {
    loadProducts();
    getProductsPayNowQrUrl(salonId).then(url => setPaynowQrUrl(url || ''));
  }, [salonId]);

  // Sync inventory inputs from products when they first load
  useEffect(() => {
    if (products.length > 0) {
      const initial = {};
      products.forEach(p => {
        initial[p.id] = {
          stockLevel: p.stockLevel !== null && p.stockLevel !== undefined ? String(p.stockLevel) : '',
          stockRenewalDate: p.stockRenewalDate || ''
        };
      });
      setInventoryData(initial);
    }
  }, [products]);

  // Check renewal alerts when inventory tab opens
  useEffect(() => {
    if (activeTab === 'inventory') checkRenewalAlerts();
  }, [activeTab, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const loaded = await getSalonProducts(salonId);
      setProducts(loaded);
      setError('');
    } catch (err) {
      console.error('Error loading products:', err);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkRenewalAlerts = async () => {
    for (const product of products) {
      if (!product.stockRenewalDate) continue;
      const days = daysUntil(product.stockRenewalDate);
      if (days !== null && days <= 7 && days >= 0) {
        await upsertProductAlert(salonId, product.id, product.name, 'renewal_approaching', {
          renewalDate: product.stockRenewalDate,
          message: `Stock renewal date for "${product.name}" is in ${days} day${days !== 1 ? 's' : ''}.`
        });
      } else if (days !== null && days > 7) {
        // If previously flagged, resolve it once date is no longer within 7 days
        await resolveProductAlert(`${salonId}_${product.id}_renewal_approaching`).catch(() => {});
      }
    }
  };

  // ── Validation ──
  const validateField = (name, value) => {
    let msg = '';
    if (name === 'name' && (!value || value.trim() === '')) msg = 'Product Name is required.';
    if (name === 'price' && (value === '' || value === null || Number(value) < 0)) msg = 'Price must be 0 or greater.';
    setFieldErrors(prev => ({ ...prev, [name]: msg }));
    return msg === '';
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : name === 'price' ? Number(value) : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    if (name === 'name' || name === 'price') validateField(name, newValue);
  };

  const handleLocationToggle = (locationId) => {
    setFormData(prev => {
      const ids = prev.locationIds.includes(locationId)
        ? prev.locationIds.filter(id => id !== locationId)
        : [...prev.locationIds, locationId];
      return { ...prev, locationIds: ids };
    });
  };

  // ── Image upload ──
  // Dropping or selecting a file opens the positioning dialog; the upload
  // starts once the admin confirms how the image is framed.
  const handleImageDrop = useCallback((acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) return;
    setImageUploadError('');
    setCropFile(acceptedFiles[0]);
  }, []);

  const handleCropConfirm = useCallback(async (file) => {
    setCropFile(null);
    setImageUploading(true);
    setImageUploadError('');
    try {
      const timestamp = Date.now();
      const safeName = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storageRef = ref(storage, `salons/${salonId}/products/${safeName}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      setFormData(prev => ({ ...prev, imageUrl: downloadURL }));
    } catch (err) {
      console.error('Image upload error:', err);
      setImageUploadError('Failed to upload image. Please try again.');
    } finally {
      setImageUploading(false);
    }
  }, [salonId]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleImageDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    maxFiles: 1,
    disabled: imageUploading
  });

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    setFormData(prev => ({ ...prev, imageUrl: '' }));
    setImageUploadError('');
  };

  // ── CRUD ──
  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setFieldErrors({ name: '', price: '' });
    setImageUploadError('');
  };

  const handleAddProduct = () => {
    setIsEditing(true);
    setEditingProductId(null);
    resetForm();
    setError('');
  };

  const handleEditProduct = (product) => {
    setIsEditing(true);
    setEditingProductId(product.id);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      category: product.category || 'Hair Care',
      price: typeof product.price === 'number' ? product.price : Number(product.price) || 0,
      imageUrl: product.imageUrl || '',
      locationIds: product.locationIds || [],
      isActive: product.isActive !== false
    });
    setFieldErrors({ name: '', price: '' });
    setImageUploadError('');
    setError('');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingProductId(null);
  };

  const handleSaveProduct = async () => {
    const nameOk = validateField('name', formData.name);
    const priceOk = validateField('price', formData.price);
    if (!nameOk || !priceOk) { setError('Please fix the validation errors before saving.'); return; }
    if (imageUploading) { setError('Please wait for the image to finish uploading.'); return; }

    setLoading(true);
    setError('');
    try {
      if (editingProductId) {
        await updateSalonProduct(salonId, editingProductId, formData);
        setProducts(products.map(p => p.id === editingProductId ? { ...p, ...formData } : p));
      } else {
        const newId = await addSalonProduct(salonId, formData);
        setProducts([...products, { ...formData, id: newId }]);
      }
      setIsEditing(false);
      setEditingProductId(null);
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error saving product:', err);
      setError('Failed to save product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      setLoading(true);
      await deleteSalonProduct(salonId, productId);
      setProducts(products.filter(p => p.id !== productId));
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error deleting product:', err);
      setError('Failed to delete product. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    try {
      await updateSalonProduct(salonId, productId, { isActive: !product.isActive });
      setProducts(products.map(p => p.id === productId ? { ...p, isActive: !p.isActive } : p));
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Error toggling product status:', err);
      setError('Failed to update product status. Please try again.');
    }
  };

  // ── Find Prospects ──
  const buildPrimaryCriteria = () => {
    if (criteriaType === 'spend_product') {
      if (!spendThreshold) { setProspectsError('Please enter a spend amount.'); return null; }
      return { type: 'spend_product', threshold: spendThreshold };
    } else if (criteriaType === 'last_purchase_date') {
      if (!lastPurchaseDate) { setProspectsError('Please select a date.'); return null; }
      return { type: 'last_purchase_date', date: lastPurchaseDate };
    } else if (criteriaType === 'product_buyers') {
      if (!prospectsProductId) { setProspectsError('Please select a product.'); return null; }
      return { type: 'product_buyers', productId: prospectsProductId };
    } else if (criteriaType === 'category_buyers') {
      return { type: 'category_buyers', category: prospectsCategory };
    }
    return null;
  };

  const buildAdditionalCriteriaItem = (ac) => {
    if (ac.type === 'spend_product') {
      if (!ac.threshold) return null;
      return { type: 'spend_product', threshold: ac.threshold, logicalOp: ac.logicalOp };
    } else if (ac.type === 'last_purchase_date') {
      if (!ac.date) return null;
      return { type: 'last_purchase_date', date: ac.date, logicalOp: ac.logicalOp };
    } else if (ac.type === 'product_buyers') {
      if (!ac.productId) return null;
      return { type: 'product_buyers', productId: ac.productId, logicalOp: ac.logicalOp };
    } else if (ac.type === 'category_buyers') {
      return { type: 'category_buyers', category: ac.category || PRODUCT_CATEGORIES[0], logicalOp: ac.logicalOp };
    }
    return null;
  };

  const handleRunMatch = async () => {
    setProspectsError('');
    const primary = buildPrimaryCriteria();
    if (!primary) return;

    const extra = additionalCriteria
      .map(ac => buildAdditionalCriteriaItem(ac))
      .filter(Boolean);

    setProspectsLoading(true);
    try {
      const result = await getProductProspects(salonId, primary, extra, products);
      setProspectsResult(result);
      setShowProspectsModal(true);
    } catch (err) {
      console.error('Error finding prospects:', err);
      setProspectsError('Failed to find prospects. Please try again.');
    } finally {
      setProspectsLoading(false);
    }
  };

  const addAdditionalCriteria = () => {
    if (additionalCriteria.length >= 2) return;
    setAdditionalCriteria(prev => [...prev, {
      id: Date.now(),
      logicalOp: 'AND',
      type: 'last_purchase_date',
      threshold: '',
      productId: '',
      category: PRODUCT_CATEGORIES[0],
      date: ''
    }]);
  };

  const removeAdditionalCriteria = (id) => {
    setAdditionalCriteria(prev => prev.filter(ac => ac.id !== id));
  };

  const updateAdditionalCriteria = (id, updates) => {
    setAdditionalCriteria(prev => prev.map(ac => ac.id === id ? { ...ac, ...updates } : ac));
  };

  const handleLaunchCampaign = (channelType) => {
    if (!prospectsResult || !onNavigateToCampaign) return;

    // Pass full customer objects (id + displayName + email) so campaign editors can
    // populate selectedCustomers directly without an extra Firestore lookup.
    const matchedCustomers = prospectsResult.matched.filter(c => c.email);

    // Find the selected product for context
    const resolvedProductId = criteriaType === 'product_buyers'
      ? prospectsProductId
      : additionalCriteria.find(ac => ac.type === 'product_buyers')?.productId || '';
    const contextProduct = resolvedProductId
      ? products.find(p => p.id === resolvedProductId)
      : null;
    const productName = contextProduct?.name || 'our products';
    const productPrice = contextProduct?.price != null ? `$${Number(contextProduct.price).toFixed(2)}` : '';
    const productDesc = contextProduct?.description || '';

    onNavigateToCampaign(channelType, {
      productName,
      productPrice,
      productDesc,
      paynowQrUrl
    }, matchedCustomers);

    setShowProspectsModal(false);
  };

  const filteredProspects = prospectsResult?.matched?.filter(c => {
    if (!prospectsFilter) return true;
    const s = prospectsFilter.toLowerCase();
    return (c.displayName || '').toLowerCase().includes(s) || (c.email || '').toLowerCase().includes(s);
  }) || [];

  const exportProspectsCSV = () => {
    const rows = [['Name', 'Email'], ...filteredProspects.map(c => [`"${c.displayName}"`, `"${c.email}"`])];
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `prospects-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Inventory ──
  const handleInventoryChange = (productId, field, value) => {
    setInventoryData(prev => ({
      ...prev,
      [productId]: { ...prev[productId], [field]: value }
    }));
  };

  const handleSaveInventory = async (product) => {
    const pid = product.id;
    const data = inventoryData[pid] || {};
    const previousStock = product.stockLevel;
    const newStock = data.stockLevel !== '' ? Number(data.stockLevel) : null;

    setInventorySaving(prev => ({ ...prev, [pid]: true }));
    setInventoryError('');
    try {
      await updateProductInventory(salonId, pid, data);

      // Alert: stock dropped to 0 from positive
      if (previousStock > 0 && newStock === 0) {
        await upsertProductAlert(salonId, pid, product.name, 'out_of_stock', {
          message: `Stock for "${product.name}" has reached 0.`
        });
      } else if (newStock > 0) {
        // Resolve out-of-stock alert if stock is replenished
        await resolveProductAlert(`${salonId}_${pid}_out_of_stock`).catch(() => {});
      }

      // Check renewal date alert
      const renewal = data.stockRenewalDate;
      if (renewal) {
        const days = daysUntil(renewal);
        if (days !== null && days <= 7 && days >= 0) {
          await upsertProductAlert(salonId, pid, product.name, 'renewal_approaching', {
            renewalDate: renewal,
            message: `Stock renewal date for "${product.name}" is in ${days} day${days !== 1 ? 's' : ''}.`
          });
        }
      }

      // Update local product state
      setProducts(prev => prev.map(p => p.id === pid
        ? { ...p, stockLevel: newStock, stockRenewalDate: data.stockRenewalDate || null }
        : p
      ));

      setInventorySaved(prev => ({ ...prev, [pid]: true }));
      setTimeout(() => setInventorySaved(prev => ({ ...prev, [pid]: false })), 2500);
    } catch (err) {
      console.error('Error saving inventory:', err);
      setInventoryError('Failed to save inventory. Please try again.');
    } finally {
      setInventorySaving(prev => ({ ...prev, [pid]: false }));
    }
  };

  // ── Group products by category ──
  const byCategory = {};
  products.forEach(p => {
    const cat = p.category || 'Other';
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(p);
  });

  if (loading && products.length === 0) {
    return <div className="products-editor-loading">Loading products…</div>;
  }

  return (
    <div className="products-editor">
      {/* Header */}
      <div className="editor-page-header">
        <h2>Products</h2>
        <div className="editor-page-actions">
          {savedMessage && (
            <span className={`editor-save-msg${savedMessage.toLowerCase().includes('error') ? ' error' : ''}`}>
              {savedMessage}
            </span>
          )}
          <button className="page-action-btn page-action-preview" type="button" onClick={onPreview}>
            Live Preview
          </button>
        </div>
      </div>
      <p className="editor-description">
        Manage your salon's retail products, discover potential customers, and track inventory levels.
      </p>

      {/* Internal tab pills */}
      <div className="products-tabs">
        <button className={`products-tab-btn${activeTab === 'catalog' ? ' active' : ''}`} onClick={() => { setActiveTab('catalog'); setIsEditing(false); }}>Products</button>
        <button className={`products-tab-btn${activeTab === 'prospects' ? ' active' : ''}`} onClick={() => { setActiveTab('prospects'); setIsEditing(false); }}>Find Prospects</button>
        <button className={`products-tab-btn${activeTab === 'inventory' ? ' active' : ''}`} onClick={() => { setActiveTab('inventory'); setIsEditing(false); }}>Manage Inventory</button>
        <button className={`products-tab-btn${activeTab === 'delivery' ? ' active' : ''}`} onClick={() => { setActiveTab('delivery'); setIsEditing(false); }}>Delivery Charges</button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* ═══════════════ TAB 1: CATALOG ═══════════════ */}
      {activeTab === 'catalog' && !isEditing && (
        <>
          <div className="service-list-actions">
            <button className="add-button" onClick={handleAddProduct}>Add New Product</button>
          </div>

          {products.length === 0 ? (
            <div className="no-services">
              <p>No products have been added yet. Click the button above to get started.</p>
            </div>
          ) : (
            <div className="services-list">
              {Object.keys(byCategory).map(category => (
                <div key={category} className="product-category">
                  <h3>{category}</h3>
                  <div className="product-items">
                    {byCategory[category].map(product => (
                      <div key={product.id} className={`product-item${!product.isActive ? ' inactive' : ''}`}>
                        <div className="product-content">
                          <div className="product-header">
                            <div className="product-admin-thumbnail">
                              {product.imageUrl
                                ? <img src={product.imageUrl} alt={product.name} />
                                : <div className="product-thumb-placeholder"><span>No Image</span></div>
                              }
                            </div>
                            <div className="product-info">
                              <h4 className="product-name">
                                {product.name}
                                {!product.isActive && <span className="inactive-badge">Inactive</span>}
                              </h4>
                              <p className="product-meta">
                                ${Number(product.price || 0).toFixed(2)}
                                {isMultiLocation && product.locationIds?.length > 0 && (
                                  <> · {product.locationIds.length} location{product.locationIds.length > 1 ? 's' : ''}</>
                                )}
                              </p>
                            </div>
                          </div>
                          {product.description && (
                            <p className="product-description">{product.description}</p>
                          )}
                        </div>
                        <div className="service-actions">
                          <button className="edit-button" onClick={() => handleEditProduct(product)}>Edit</button>
                          <button
                            className={product.isActive ? 'remove-button' : 'add-button'}
                            onClick={() => handleToggleActive(product.id)}
                          >
                            {product.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button className="remove-button" onClick={() => handleDeleteProduct(product.id)}>Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ═══════════════ ADD/EDIT FORM ═══════════════ */}
      {activeTab === 'catalog' && isEditing && (
        <div className="product-form service-form">
          <h3>{editingProductId ? 'Edit Product' : 'Add New Product'}</h3>

          {/* Name */}
          <div className="form-group">
            <label htmlFor="prod-name">Product Name<span className="required-asterisk">*</span></label>
            <input
              id="prod-name" type="text" name="name"
              value={formData.name}
              onChange={handleInputChange}
              onBlur={e => validateField('name', e.target.value)}
              className={fieldErrors.name ? 'input-error' : ''}
            />
            {fieldErrors.name && <span className="field-error-message">{fieldErrors.name}</span>}
          </div>

          {/* Image */}
          <div className="form-group">
            <label>Product Image</label>
            <div
              {...getRootProps()}
              className={`product-image-dropzone${isDragActive ? ' drag-active' : ''}${imageUploading ? ' uploading' : ''}`}
            >
              <input {...getInputProps()} />
              {imageUploading ? (
                <div className="product-image-dropzone-placeholder">
                  <span className="upload-icon">⏳</span>
                  <p>Uploading image…</p>
                </div>
              ) : formData.imageUrl ? (
                <div className="product-image-upload-preview">
                  <img src={formData.imageUrl} alt="Product preview" />
                  <div className="product-image-overlay">
                    <p>Drop a new image to replace, or click to select</p>
                    <button type="button" className="remove-image-button" onClick={handleRemoveImage}>Remove Image</button>
                  </div>
                </div>
              ) : (
                <div className="product-image-dropzone-placeholder">
                  <span className="upload-icon">📷</span>
                  <p>{isDragActive ? 'Drop the image here' : 'Drop a product image here, or click to select'}</p>
                  <p className="upload-hint">JPG, PNG, WebP · Recommended: 800 × 500 px</p>
                </div>
              )}
            </div>
            {imageUploadError && <span className="field-error-message">{imageUploadError}</span>}
          </div>

          <div className="form-row">
            {/* Category */}
            <div className="form-group">
              <label htmlFor="prod-category">Category</label>
              <select id="prod-category" name="category" value={formData.category} onChange={handleInputChange}>
                {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {/* Price */}
            <div className="form-group">
              <label htmlFor="prod-price">Price ($)<span className="required-asterisk">*</span></label>
              <input
                id="prod-price" type="number" name="price"
                value={formData.price}
                onChange={handleInputChange}
                onBlur={e => validateField('price', e.target.value)}
                className={fieldErrors.price ? 'input-error' : ''}
                min="0" step="0.01"
              />
              {fieldErrors.price && <span className="field-error-message">{fieldErrors.price}</span>}
            </div>
          </div>

          {/* Description */}
          <div className="form-group">
            <label htmlFor="prod-desc">Description</label>
            <textarea
              id="prod-desc" name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="3"
              placeholder="Describe the product, its benefits, or usage instructions…"
            />
          </div>

          {/* Locations (only for multi-location salons) */}
          {isMultiLocation && (
            <div className="form-group">
              <label>Available At (leave empty for all locations)</label>
              <div className="location-checkboxes">
                {locations.map(loc => (
                  <label key={loc.locationId} className="location-checkbox-item">
                    <input
                      type="checkbox"
                      checked={formData.locationIds.includes(loc.locationId)}
                      onChange={() => handleLocationToggle(loc.locationId)}
                    />
                    {loc.locationName || loc.locationId}
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Active */}
          <div className="form-group checkbox-group">
            <input type="checkbox" id="prod-active" name="isActive" checked={formData.isActive} onChange={handleInputChange} />
            <label htmlFor="prod-active">Product is active and visible on the customer portal</label>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={handleCancelEdit}>Cancel</button>
            <button type="button" className="save-button" onClick={handleSaveProduct} disabled={loading || imageUploading}>
              {loading ? 'Saving…' : imageUploading ? 'Uploading image…' : editingProductId ? 'Update Product' : 'Add Product'}
            </button>
          </div>
        </div>
      )}

      {/* ═══════════════ TAB 2: FIND PROSPECTS ═══════════════ */}
      {activeTab === 'prospects' && (
        <div className="prospects-section">
          <h3>Find Prospects</h3>
          <p className="prospects-description">
            Select matching criteria to find customers who are most likely to be interested in a product.
            Once matched, you can launch a targeted campaign directly from the results.
          </p>

          {/* ── Primary criteria ── */}
          <div className="prospects-criteria-block">
            <div className="prospects-criteria-block-label">Match Criteria</div>

            <div className="prospects-criteria-group">
              <label>Criteria type</label>
              <select value={criteriaType} onChange={e => { setCriteriaType(e.target.value); setSpendThreshold(''); setLastPurchaseDate(''); setProspectsProductId(''); }}>
                <option value="spend_product">Customers with Spend &gt; A Certain Amount on Product</option>
                <option value="last_purchase_date">Last Purchase Date of Product</option>
                <option value="product_buyers">Previous buyers of a specific product</option>
                <option value="category_buyers">Previous buyers of a product category</option>
              </select>
            </div>

            {criteriaType === 'spend_product' && (
              <div className="prospects-criteria-group">
                <label>Minimum spend on product ($)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="e.g. 100"
                  value={spendThreshold}
                  onChange={e => setSpendThreshold(e.target.value)}
                />
              </div>
            )}

            {criteriaType === 'last_purchase_date' && (
              <div className="prospects-criteria-group">
                <label>Last purchase date (on or before)</label>
                <input
                  type="date"
                  value={lastPurchaseDate}
                  onChange={e => setLastPurchaseDate(e.target.value)}
                />
              </div>
            )}

            {criteriaType === 'product_buyers' && (
              <div className="prospects-criteria-group">
                <label>Product</label>
                <select value={prospectsProductId} onChange={e => setProspectsProductId(e.target.value)}>
                  <option value="">— Select product —</option>
                  {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}

            {criteriaType === 'category_buyers' && (
              <div className="prospects-criteria-group">
                <label>Product category</label>
                <select value={prospectsCategory} onChange={e => setProspectsCategory(e.target.value)}>
                  {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
          </div>

          {/* ── Additional criteria (up to 2) ── */}
          {additionalCriteria.map((ac, idx) => (
            <div key={ac.id} className="prospects-criteria-block prospects-criteria-extra">
              <div className="prospects-criteria-block-header">
                <select
                  className="prospects-logical-op"
                  value={ac.logicalOp}
                  onChange={e => updateAdditionalCriteria(ac.id, { logicalOp: e.target.value })}
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>
                <span className="prospects-criteria-block-label">Additional Criteria {idx + 1}</span>
                <button className="prospects-remove-criteria-btn" onClick={() => removeAdditionalCriteria(ac.id)}>×</button>
              </div>

              <div className="prospects-criteria-group">
                <label>Criteria type</label>
                <select value={ac.type} onChange={e => updateAdditionalCriteria(ac.id, { type: e.target.value, threshold: '', date: '', productId: '', category: PRODUCT_CATEGORIES[0] })}>
                  <option value="spend_product">Customers with Spend &gt; A Certain Amount on Product</option>
                  <option value="last_purchase_date">Last Purchase Date of Product</option>
                  <option value="product_buyers">Previous buyers of a specific product</option>
                  <option value="category_buyers">Previous buyers of a product category</option>
                </select>
              </div>

              {ac.type === 'spend_product' && (
                <div className="prospects-criteria-group">
                  <label>Minimum spend on product ($)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="e.g. 100"
                    value={ac.threshold}
                    onChange={e => updateAdditionalCriteria(ac.id, { threshold: e.target.value })}
                  />
                </div>
              )}

              {ac.type === 'last_purchase_date' && (
                <div className="prospects-criteria-group">
                  <label>Last purchase date (on or before)</label>
                  <input
                    type="date"
                    value={ac.date}
                    onChange={e => updateAdditionalCriteria(ac.id, { date: e.target.value })}
                  />
                </div>
              )}

              {ac.type === 'product_buyers' && (
                <div className="prospects-criteria-group">
                  <label>Product</label>
                  <select value={ac.productId} onChange={e => updateAdditionalCriteria(ac.id, { productId: e.target.value })}>
                    <option value="">— Select product —</option>
                    {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}

              {ac.type === 'category_buyers' && (
                <div className="prospects-criteria-group">
                  <label>Product category</label>
                  <select value={ac.category} onChange={e => updateAdditionalCriteria(ac.id, { category: e.target.value })}>
                    {PRODUCT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
            </div>
          ))}

          {additionalCriteria.length < 2 && (
            <button className="prospects-add-criteria-btn" onClick={addAdditionalCriteria}>
              + Add Another Criteria
            </button>
          )}

          <button
            className="prospects-run-btn"
            onClick={handleRunMatch}
            disabled={prospectsLoading}
          >
            {prospectsLoading ? 'Matching…' : 'Run Match'}
          </button>

          {prospectsError && <p className="prospects-error">{prospectsError}</p>}

          {!onNavigateToCampaign && (
            <div className="comms-disabled-note">
              Communications Management module is required to send campaigns. Please enable it via the Module Manager.
            </div>
          )}
        </div>
      )}

      {/* ═══════════════ TAB 3: MANAGE INVENTORY ═══════════════ */}
      {activeTab === 'inventory' && (
        <div>
          <p className="inventory-description">
            Track current stock levels and renewal dates for each product.
            Alerts are automatically raised when stock reaches 0 or a renewal date is within 7 days.
          </p>

          {inventoryError && <div className="error-message">{inventoryError}</div>}

          {products.length === 0 ? (
            <div className="inventory-empty">No products yet. Add products in the Products tab first.</div>
          ) : (
            <div className="inventory-table-wrap">
              <table className="inventory-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Stock Level</th>
                    <th>Renewal Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map(product => {
                    const inv = inventoryData[product.id] || {};
                    const stockNum = inv.stockLevel !== '' && inv.stockLevel !== undefined ? Number(inv.stockLevel) : null;
                    const days = daysUntil(inv.stockRenewalDate);
                    return (
                      <tr key={product.id}>
                        <td>
                          <div className="inventory-product-name">{product.name}</div>
                          <div className="inventory-product-cat">{product.category}</div>
                        </td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            className={`inventory-input${stockNum === 0 ? ' inventory-stock-zero' : ''}`}
                            value={inv.stockLevel ?? ''}
                            onChange={e => handleInventoryChange(product.id, 'stockLevel', e.target.value)}
                            placeholder="—"
                          />
                        </td>
                        <td>
                          <input
                            type="date"
                            className={`inventory-date-input${(days !== null && days <= 7 && days >= 0) ? ' inventory-renewal-near' : ''}`}
                            value={inv.stockRenewalDate || ''}
                            onChange={e => handleInventoryChange(product.id, 'stockRenewalDate', e.target.value)}
                          />
                          {days !== null && days <= 7 && days >= 0 && (
                            <div className="inventory-renewal-near" style={{ fontSize: '0.75rem', marginTop: 3 }}>
                              Due in {days} day{days !== 1 ? 's' : ''}
                            </div>
                          )}
                        </td>
                        <td>
                          {inventorySaved[product.id] ? (
                            <span className="inventory-saved-note">Saved ✓</span>
                          ) : (
                            <button
                              className="inventory-save-btn"
                              onClick={() => handleSaveInventory(product)}
                              disabled={inventorySaving[product.id]}
                            >
                              {inventorySaving[product.id] ? 'Saving…' : 'Save'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'delivery' && (
        <DeliveryChargesEditor salonId={salonId} onUpdate={onUpdate} />
      )}

      {/* ═══════════════ PROSPECTS MODAL ═══════════════ */}
      {showProspectsModal && prospectsResult && (
        <div className="prospects-modal-overlay" onClick={() => setShowProspectsModal(false)}>
          <div className="prospects-modal" onClick={e => e.stopPropagation()}>
            <div className="prospects-modal-header">
              <h3>Matched Customers</h3>
              <button className="prospects-modal-close" onClick={() => setShowProspectsModal(false)}>×</button>
            </div>

            <div className="prospects-modal-summary">
              <div className="prospects-summary-item">
                <span className="prospects-summary-label">Matched</span>
                <span className="prospects-summary-value highlight">{prospectsResult.matched.length}</span>
              </div>
              <div className="prospects-summary-item">
                <span className="prospects-summary-label">Total Customers</span>
                <span className="prospects-summary-value">{prospectsResult.total}</span>
              </div>
              <div className="prospects-summary-item">
                <span className="prospects-summary-label">Coverage</span>
                <span className="prospects-summary-value highlight">
                  {prospectsResult.total > 0
                    ? `${((prospectsResult.matched.length / prospectsResult.total) * 100).toFixed(1)}%`
                    : '0.0%'}
                </span>
              </div>
            </div>

            <div className="prospects-modal-body">
              {prospectsResult.matched.length > 0 ? (
                <>
                  <div className="prospects-filter-row">
                    <input
                      type="text"
                      placeholder="Search by name or email…"
                      value={prospectsFilter}
                      onChange={e => setProspectsFilter(e.target.value)}
                    />
                    <button className="prospects-export-btn" onClick={exportProspectsCSV}>📥 Export CSV</button>
                  </div>
                  <div className="prospects-table-container">
                    <table className="prospects-table">
                      <thead>
                        <tr>
                          <th>Customer Name</th>
                          <th>Email</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProspects.slice(0, 100).map((c, i) => (
                          <tr key={c.id || i}>
                            <td>{c.displayName || 'N/A'}</td>
                            <td>{c.email || 'N/A'}</td>
                          </tr>
                        ))}
                        {filteredProspects.length > 100 && (
                          <tr>
                            <td colSpan="2" style={{ textAlign: 'center', color: '#888', fontStyle: 'italic', fontSize: '0.82rem' }}>
                              Showing first 100 of {filteredProspects.length} — export CSV for full list
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div className="prospects-empty">
                  <p>No matching customers found for the selected criteria.</p>
                  {criteriaType !== 'spend_product' && (
                    <p style={{ fontSize: '0.82rem', marginTop: 8, color: '#bbb' }}>
                      Note: Purchase history for products is recorded once product transactions are logged in the system.
                    </p>
                  )}
                </div>
              )}
            </div>

            {prospectsResult.matched.length > 0 && onNavigateToCampaign && (
              <div className="prospects-modal-footer">
                <h4>Launch campaign for matched customers:</h4>
                <div className="prospects-campaign-btns">
                  <button className="prospects-campaign-btn email" onClick={() => handleLaunchCampaign('email')}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="4" width="20" height="16" rx="2"/>
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                    </svg>
                    Email Campaign
                  </button>
                  <button className="prospects-campaign-btn whatsapp" onClick={() => handleLaunchCampaign('whatsapp')}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    WhatsApp Campaign
                  </button>
                  <button className="prospects-campaign-btn push" onClick={() => handleLaunchCampaign('push')}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
                      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
                    </svg>
                    Push Notification
                  </button>
                </div>
              </div>
            )}

            {prospectsResult.matched.length > 0 && !onNavigateToCampaign && (
              <div className="prospects-modal-footer">
                <div className="comms-disabled-note">
                  Enable Communications Management to send campaigns to matched customers.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {cropFile && (
        <ImageCropModal
          file={cropFile}
          kind="product"
          onCancel={() => setCropFile(null)}
          onConfirm={handleCropConfirm}
        />
      )}
    </div>
  );
}

export default ProductsEditor;
